import { TypedDocumentNode, gql } from '@apollo/client';
import { ComponentProps } from 'react';

import { uniqueBy } from '@sorare/core/src/lib/arrays';
import { WithRewardRange } from '@sorare/core/src/lib/rewards';

import {
  RewardRange,
  RewardRangeVariant,
} from 'components/leaderboard/RewardRange';
import { ClickableAnyRewardConfig } from 'components/rewards/ClickableAnyRewardConfig';
import { Variant } from 'components/rewards/RewardOverview/types';

import { RankRewardTitle } from '../RankRewardTitle';
import { Ranking } from '../Ranking';
import type { RankReward_so5Leaderboard } from './__generated__/index.graphql';
import {
  RankReward_so5Ranking,
  RankReward_so5RewardConfig,
} from './__generated__/index.graphql';

type Props = {
  so5Leaderboard: RankReward_so5Leaderboard;
  so5RewardConfig: WithRewardRange<RankReward_so5RewardConfig>;
  so5Ranking?: RankReward_so5Ranking;
  overview: boolean;
  isMyRange: boolean;
  isMyNextRange: boolean;
  rewardOverviewVariant: Variant;
  rewardRangeVariant: RewardRangeVariant;
  onClickRanking?: (so5RankingId: string) => void;
  hideRankings?: boolean;
} & Pick<ComponentProps<typeof RewardRange>, 'withSeparator'>;

export const RankReward = ({
  so5Leaderboard,
  so5RewardConfig,
  so5Ranking,
  overview,
  isMyRange,
  isMyNextRange,
  rewardOverviewVariant,
  rewardRangeVariant,
  onClickRanking,
  hideRankings,
  withSeparator,
}: Props) => {
  const isMyRangeOrMyNextRange = isMyRange || isMyNextRange;
  const shouldDisplay = overview ? isMyRangeOrMyNextRange : true;

  if (!shouldDisplay) {
    return null;
  }

  return (
    <RewardRange
      key={so5RewardConfig.startRank}
      variant={rewardRangeVariant}
      title={<RankRewardTitle {...so5RewardConfig} />}
      rewards={so5RewardConfig.rewardConfigs.map((rewardConfig, i) => {
        return (
          <ClickableAnyRewardConfig
            // eslint-disable-next-line react/no-array-index-key
            key={i}
            variant={rewardOverviewVariant}
            anyRewardConfig={rewardConfig}
            rarity={so5Leaderboard.mainRarityType || undefined}
            so5League={so5Leaderboard.so5League}
          />
        );
      })}
      withSeparator={withSeparator}
    >
      {!hideRankings &&
        uniqueBy(
          ranking => (ranking.ranking || 0).toString(),
          [
            so5RewardConfig.fromSo5Ranking,
            isMyRange && so5Ranking,
            so5RewardConfig.toSo5Ranking,
          ].filter(Boolean)
        ).map(ranking => (
          <Ranking
            key={ranking.ranking}
            so5Ranking={ranking}
            isMyRanking={ranking.ranking === so5Ranking?.ranking}
            onClick={() =>
              onClickRanking?.(ranking.so5Lineup.so5LeaderboardContender.slug)
            }
          />
        ))}
    </RewardRange>
  );
};
RankReward.fragments = {
  so5Leaderboard: gql`
    fragment RankReward_so5Leaderboard on So5Leaderboard {
      slug
      mainRarityType
      so5League {
        slug
        ...ClickableAnyRewardConfig_so5League
      }
    }
    ${ClickableAnyRewardConfig.fragments.so5League}
  ` as TypedDocumentNode<RankReward_so5Leaderboard>,
  so5RewardConfig: gql`
    fragment RankReward_so5RewardConfig on So5RewardConfig {
      ranks
      rankPct
      rewardConfigs {
        id
        ...ClickableAnyRewardConfig_anyRewardConfigInterface
      }
      fromSo5Ranking {
        id
        so5Lineup {
          id
          so5LeaderboardContender {
            slug
          }
        }
        ...RankRewardsRanking_so5Ranking
      }
      toSo5Ranking {
        id
        so5Lineup {
          id
          so5LeaderboardContender {
            slug
          }
        }
        ...RankRewardsRanking_so5Ranking
      }
    }
    ${ClickableAnyRewardConfig.fragments.anyRewardConfigInterface}
    ${Ranking.fragments.so5Ranking}
  ` as TypedDocumentNode<RankReward_so5RewardConfig>,
  so5Ranking: gql`
    fragment RankReward_so5Ranking on So5Ranking {
      id
      so5Lineup {
        id
        so5LeaderboardContender {
          slug
        }
      }
      ...RankRewardsRanking_so5Ranking
    }
    ${Ranking.fragments.so5Ranking}
  ` as TypedDocumentNode<RankReward_so5Ranking>,
};
